import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { UserSettingsService } from "@/features/Users/services/userSettingsService";

// Chave para armazenar preferência de idioma no localStorage
const LANGUAGE_PREFERENCE_KEY = 'prodgy-user-language-preference';

// Função para obter a preferência de idioma
const getUserLanguagePreference = (): string | null => {
  try {
    return localStorage.getItem(LANGUAGE_PREFERENCE_KEY);
  } catch (e) {
    console.error("Não foi possível acessar localStorage", e);
    return null;
  }
};

// Função para salvar a preferência de idioma
const saveUserLanguagePreference = (language: string): void => {
  try {
    localStorage.setItem(LANGUAGE_PREFERENCE_KEY, language);
  } catch (e) {
    console.error("Não foi possível salvar no localStorage", e);
  }
};

// Função auxiliar para detectar o idioma do navegador
const detectBrowserLanguage = (): string => {
  const browserLang = navigator.language.split('-')[0]; // Obter apenas a parte principal (en-US -> en)
  // Verificar se é um dos idiomas suportados, senão retornar inglês
  return ['en', 'pt'].includes(browserLang) ? browserLang : 'en';
};

export const useLanguageLoader = () => {
  const { user } = useAuth();
  const { i18n } = useTranslation();

  useEffect(() => {
    // Flag para controlar se o componente ainda está montado
    let isMounted = true;
    
    const loadUserLanguage = async () => {
      // Verificar se existe uma preferência de idioma salva no localStorage
      const savedPreference = getUserLanguagePreference();
      
      // Detectar o idioma do navegador como fallback
      const browserLanguage = detectBrowserLanguage();
      
      // Determinar qual idioma usar:
      // 1. Se tem preferência salva (usuário já usou o sistema), usar ela
      // 2. Senão, usar idioma do navegador (primeira visita)
      const preferredLanguage = savedPreference || browserLanguage;
      
      if (!user) {
        // Se não houver usuário, usar a preferência salva ou o idioma do navegador
        await i18n.changeLanguage(preferredLanguage);
        return;
      }

      // Usar a preferência salva ou o idioma do navegador como fallback temporário
      await i18n.changeLanguage(preferredLanguage);
      
      // Implementar retry logic com delay exponencial
      const MAX_RETRIES = 3;
      const INITIAL_DELAY = 1000; // 1 segundo
      
      let retryCount = 0;
      let success = false;
      
      while (!success && retryCount < MAX_RETRIES && isMounted) {
        try {
          // Tentar obter configurações do usuário
          const userSettings = await UserSettingsService.getByUserId(user.id);
          
          if (userSettings?.language && isMounted) {
            await i18n.changeLanguage(userSettings.language);
            // Salvar a preferência do usuário logado no localStorage
            saveUserLanguagePreference(userSettings.language);
            success = true;
          } else if (isMounted) {
            // Se não tiver configuração de idioma, usar a preferência salva ou o idioma do navegador
            const preferredLang = getUserLanguagePreference() || detectBrowserLanguage();
            await i18n.changeLanguage(preferredLang);
            await UserSettingsService.updateByUserId(user.id, { language: preferredLang });
            saveUserLanguagePreference(preferredLang);
            success = true;
          }
        } catch (error) {
          try {
            if (retryCount === MAX_RETRIES - 1 && isMounted) {
              // Na última tentativa, tente criar uma nova configuração com o idioma do navegador
              const browserLang = detectBrowserLanguage();
              await UserSettingsService.create({
                language: browserLang
              });
              success = true;
            } else {
              // Aumentar o delay exponencialmente: 1s, 2s, 4s, etc.
              const delay = INITIAL_DELAY * Math.pow(2, retryCount);
              await new Promise(resolve => setTimeout(resolve, delay));
              retryCount++;
            }
          } catch (createError) {
            // Ignorar erro na criação - o backend já pode ter criado o registro
            if (retryCount === MAX_RETRIES - 1) {
              console.warn("Não foi possível carregar/criar configurações de usuário após várias tentativas");
            }
            retryCount++;
          }
        }
      }
    };

    loadUserLanguage();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [user, i18n]);
};
